import type { GuideStep } from './types'

export type GuideRuntimeErrorCode
  = | 'route-status-error'
    | 'route-ready-timeout'
    | 'route-not-found'
    | 'route-ambiguous'
    | 'route-not-registered'
    | 'router-resolve-failed'
    | 'navigation-error'
    | 'route-change-timeout'
    | 'page-mount-timeout'
    | 'target-not-found'

export interface GuideRuntimeFailure {
  ok: false
  code: GuideRuntimeErrorCode
  detail?: string
}

export interface GuideRuntimeSuccess<T> {
  ok: true
  value: T
}

export type GuideRuntimeResult<T> = GuideRuntimeSuccess<T> | GuideRuntimeFailure

export interface GuideMountedPage {
  root: HTMLElement
  anchor: HTMLElement | null
  source: 'step-selector' | 'page-root'
}

export interface WaitForGuidePageMountedOptions {
  step: Pick<GuideStep, 'id' | 'selectors'>
  routeMatched: () => boolean
  getFallbackRoot: () => HTMLElement | null
  previousRoot?: HTMLElement | null
  acceptCurrentRoot?: boolean
  timeout?: number
  interval?: number
}

export function guideRuntimeSuccess<T>(value: T): GuideRuntimeSuccess<T> {
  return { ok: true, value }
}

export function guideRuntimeFailure(
  code: GuideRuntimeErrorCode,
  detail?: string,
): GuideRuntimeFailure {
  return { ok: false, code, detail }
}

export function isGuideRuntimeFailure<T>(
  result: GuideRuntimeResult<T>,
): result is GuideRuntimeFailure {
  return !result.ok
}

function isVisibleGuideElement(element: HTMLElement | null | undefined) {
  if (!element || !element.isConnected) {
    return false
  }
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) {
    return false
  }
  const style = window.getComputedStyle(element)
  return style.display !== 'none'
    && style.visibility !== 'hidden'
    && style.visibility !== 'collapse'
    && style.opacity !== '0'
}

function findVisibleGuideElement(selectors: string[]) {
  for (const selector of selectors) {
    if (!selector) {
      continue
    }
    let elements: HTMLElement[] = []
    try {
      elements = Array.from(document.querySelectorAll<HTMLElement>(selector))
    }
    catch {
      continue
    }
    const element = elements.find(item => isVisibleGuideElement(item))
    if (element) {
      return element
    }
  }
  return null
}

/**
 * 根据已经出现的步骤目标反向定位当前 router-view 页面根节点。
 * 不再依赖“.main 最后一个可见子节点”的 DOM 顺序，因此兼容多标签页与 KeepAlive。
 */
function resolvePageRootFromAnchor(anchor: HTMLElement, fallbackRoot: HTMLElement | null) {
  const main = document.querySelector<HTMLElement>('#app-main .main-container .main')
  if (main?.contains(anchor)) {
    let current: HTMLElement = anchor
    while (current.parentElement && current.parentElement !== main) {
      current = current.parentElement
    }
    if (current.parentElement === main && isVisibleGuideElement(current)) {
      return current
    }
  }

  const semanticRoot = anchor.closest<HTMLElement>([
    '[data-guide-page]',
    '.absolute-container',
    '.main-page',
    '.fa-page-main',
    '.dashboard-page',
  ].join(', '))
  if (semanticRoot && isVisibleGuideElement(semanticRoot)) {
    return semanticRoot
  }

  if (fallbackRoot && fallbackRoot.contains(anchor) && isVisibleGuideElement(fallbackRoot)) {
    return fallbackRoot
  }
  return anchor
}

/**
 * 等待当前步骤所属页面完成挂载。
 *
 * 优先以步骤已经声明的 selector 作为页面挂载证据；找到后再反向取得页面根节点。
 * 只有目标需要后续 before 行为才会出现、或页面没有可提前出现的 selector 时，
 * 才使用新的 router-view 根节点作为兜底。这样既能修复 KeepAlive 下页面根节点误判，
 * 又不会改变原有弹窗、抽屉、页签步骤的执行顺序。
 */
export async function waitForGuidePageMounted(
  options: WaitForGuidePageMountedOptions,
): Promise<GuideRuntimeResult<GuideMountedPage>> {
  const timeout = options.timeout ?? 15000
  const interval = options.interval ?? 80
  const startedAt = Date.now()
  let routeWasMatched = false

  while (Date.now() - startedAt < timeout) {
    if (!options.routeMatched()) {
      await new Promise(resolve => window.setTimeout(resolve, interval))
      continue
    }
    routeWasMatched = true

    const fallbackRoot = options.getFallbackRoot()
    const anchor = findVisibleGuideElement(options.step.selectors)
    if (anchor) {
      return guideRuntimeSuccess({
        root: resolvePageRootFromAnchor(anchor, fallbackRoot),
        anchor,
        source: 'step-selector',
      })
    }

    if (fallbackRoot && isVisibleGuideElement(fallbackRoot)) {
      const isNewRoot = !options.previousRoot
        || fallbackRoot !== options.previousRoot
        || !options.previousRoot.isConnected
      if (isNewRoot || options.acceptCurrentRoot) {
        return guideRuntimeSuccess({
          root: fallbackRoot,
          anchor: null,
          source: 'page-root',
        })
      }
    }

    await new Promise(resolve => window.setTimeout(resolve, interval))
  }

  return guideRuntimeFailure(
    routeWasMatched ? 'page-mount-timeout' : 'route-change-timeout',
    `${options.step.id}: ${options.step.selectors.join(', ')}`,
  )
}
